import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { trackPurchaseConversion, trackEvent } from "@/lib/analytics";
import ReferralInline from "@/components/ReferralInline";
import AnnouncementBar from "@/components/AnnouncementBar";
import PricingCards from "@/components/PricingCards";
import { usePublicSettings } from "@/lib/siteSettings";
import { Sparkles, CheckCircle2, Loader2, Gift, ShieldCheck, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const BillingPage = () => {
  const navigate = useNavigate();
  const { user, refresh, isPro, isTrialing, trialDaysLeft } = useAuth();
  const [params] = useSearchParams();
  const settings = usePublicSettings();
  const freemiumV2 = settings["features.freemium_v2"] === true;
  const pwCopy = settings["paywall_copy"] || {};
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [showRefer, setShowRefer] = useState(false);
  const [resumeStats, setResumeStats] = useState(null);

  useEffect(() => {
    trackEvent("pricing_viewed");
    axios.get(`${API}/public/pricing-cards`)
      .then((r) => setCards(r.data?.cards || []))
      .catch(() => setCards([]));
  }, []);

  const resumeMode = freemiumV2 && !isPro && !isTrialing;
  useEffect(() => {
    if (!resumeMode) return;
    Promise.all([
      axios.get(`${API}/progress`).catch(() => null),
      axios.get(`${API}/progress/focus-area`).catch(() => null),
    ]).then(([p, f]) => {
      setResumeStats({
        answered: p?.data?.progress?.questions_answered || 0,
        weakest: f?.data?.has_focus ? f.data.subject_name : null,
      });
    });
  }, [resumeMode]);

  // Confirm payment if we returned from Stripe
  useEffect(() => {
    const sessionId = params.get("session_id") || params.get("from_session");
    if (!sessionId) return;
    setCheckingStatus(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        let data;
        try {
          const res = await axios.get(`${API}/billing/subscribe/status/${sessionId}`);
          data = res.data;
        } catch (err) {
          if (err?.response?.status === 404) {
            const res = await axios.get(`${API}/billing/status/${sessionId}`);
            data = res.data;
          } else throw err;
        }
        const paid = data.payment_status === "paid" || data.payment_status === "no_payment_required" || data.status === "complete";
        if (paid) {
          await refresh();
          trackPurchaseConversion({ value: data.amount_total, currency: data.currency, transactionId: sessionId });
          trackEvent("purchase", { value: data.amount_total, currency: data.currency, transaction_id: sessionId });
          toast.success("Welcome to Pro!", { description: "All Pro features are now unlocked." });
          setShowRefer(true);
          setCheckingStatus(false);
          return;
        }
        if (data.status === "expired" || attempts >= 6) {
          toast.error("Payment not confirmed", { description: "If you paid, refresh in a minute." });
          setCheckingStatus(false);
          return;
        }
        setTimeout(poll, 2500);
      } catch { setCheckingStatus(false); }
    };
    poll();
  }, [params, refresh]);

  const startCheckout = async (card) => {
    if (!card?.plan_id) { navigate("/dashboard"); return; }
    setLoading(true);
    try {
      const value = (card.amount_cents || 0) / 100;
      const currency = card.currency || "AUD";
      trackEvent("begin_checkout", { plan_id: card.plan_id, value, currency });
      trackEvent("pro_checkout_started", { plan_id: card.plan_id, value, currency });
      const res = await axios.post(`${API}/billing/subscribe`, { plan_id: card.plan_id, origin_url: window.location.origin });
      const target = res.data?.checkout_url || res.data?.url;
      if (target) window.location.href = target;
      else { toast.error("Could not start checkout."); setLoading(false); }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  // One-click checkout from a pricing CTA (?plan=<key>) or a lifecycle link (?upgrade=1).
  const [autoUpgradeAttempted, setAutoUpgradeAttempted] = useState(false);
  const startCheckoutRef = useRef(null);
  startCheckoutRef.current = startCheckout;
  useEffect(() => {
    if (autoUpgradeAttempted) return;
    if (!user) return;
    if (isPro && !isTrialing) return;
    if (cards.length === 0) return;
    if (loading || checkingStatus) return;
    const paid = cards.filter((c) => c.plan_id);
    let planKey = params.get("plan");
    if (!planKey) { try { planKey = localStorage.getItem("passadc_pending_checkout"); } catch { /* ignore */ } }
    let target = null;
    if (planKey) target = paid.find((c) => c.plan_key === planKey);
    else if (params.get("upgrade") === "1") target = paid.find((c) => c.highlight) || paid[0];
    if (!target) return;
    try { localStorage.removeItem("passadc_pending_checkout"); } catch { /* ignore */ }
    setAutoUpgradeAttempted(true);
    if (params.get("upgrade") === "1") trackEvent("trial_ending_email_clicked", { source: "trial_ending_24h" });
    Promise.resolve().then(() => startCheckoutRef.current?.(target));
  }, [params, user, isPro, isTrialing, cards, loading, checkingStatus, autoUpgradeAttempted]);

  const cancelSubscription = async () => {
    if (!window.confirm("Cancel your Pro plan? You'll keep access until your current period ends.")) return;
    try {
      await axios.post(`${API}/billing/cancel`);
      await refresh();
      toast.success("Cancelled - Pro stays active until your current period ends.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  // ===== Pro user view (paid — trialing users still see the tier picker below) =====
  if (isPro && !isTrialing) {
    const expiry = user?.pro_until ? new Date(user.pro_until).toLocaleDateString() : "renewal";
    const isCancelled = user?.subscription_status === "cancelled";
    return (
      <div data-testid="billing-pro-state" className="max-w-2xl mx-auto text-center py-16">
        <div className="w-20 h-20 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-10 h-10 text-success" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold mb-2">You&apos;re on Pro</h1>
        <p className="text-muted-foreground mb-2">All features unlocked until {expiry}.</p>
        {isCancelled && <p className="text-warning text-sm mb-4">Your plan is cancelled - Pro stays active until {expiry}, then auto-downgrades.</p>}
        {showRefer && (
          <div className="mt-8 text-left" data-testid="post-purchase-referral">
            <p className="text-sm font-semibold text-center mb-3">One quick thing - earn free months while you study 👇</p>
            <ReferralInline variant="compact" onDismiss={() => setShowRefer(false)} />
          </div>
        )}
        <div className="flex justify-center gap-3 mt-6 flex-wrap">
          <Button onClick={() => navigate("/dashboard")} className="rounded-full">Back to dashboard</Button>
          {!isCancelled && (
            <Button data-testid="billing-cancel-btn" variant="outline" onClick={cancelSubscription} className="rounded-full">
              <XCircle className="w-4 h-4 mr-2" /> Cancel renewal
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="billing-page" className="max-w-5xl mx-auto py-8">
      <AnnouncementBar context="app" />
      {checkingStatus && (
        <div className="glass rounded-2xl p-6 border border-primary/20 mb-6 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
          <p className="text-sm">Confirming your payment...</p>
        </div>
      )}
      {loading && (
        <div className="glass rounded-2xl p-6 border border-primary/20 mb-6 flex items-center gap-3" data-testid="billing-redirecting">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
          <p className="text-sm">Redirecting to secure Stripe checkout...</p>
        </div>
      )}

      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-4">
          <Sparkles className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-medium text-primary">The practice + readiness layer</span>
        </div>
        {resumeMode ? (
          <>
            <h1 className="text-3xl md:text-4xl font-bold mb-3" data-testid="billing-resume-heading">
              {pwCopy.billing_resume_heading || "Pick up where you left off"}
            </h1>
            {resumeStats && (resumeStats.answered > 0 || resumeStats.weakest) && (
              <p className="text-muted-foreground max-w-lg mx-auto" data-testid="billing-resume-stats">
                You&apos;ve answered <strong className="text-foreground">{resumeStats.answered}</strong> questions
                {resumeStats.weakest ? <> · your weakest area is <strong className="text-foreground">{resumeStats.weakest}</strong></> : null}.
                Go Pro to unlock unlimited practice and your fix-it lessons.
              </p>
            )}
          </>
        ) : (
          <>
            <h1 className="text-3xl md:text-4xl font-bold mb-3">Add PassADC Pro to your <span className="text-primary">ADC prep</span></h1>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Sits on top of whatever you&apos;re already studying with. Practice, weak-area targeting, and a live Readiness Score no textbook can give you.
            </p>
          </>
        )}
        <div className="mt-5 flex justify-center">
          <Link to="/guarantee" data-testid="billing-guarantee-badge"
                className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/5 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10 transition">
            <ShieldCheck className="w-4 h-4" />
            Backed by the 80% Readiness Commitment
          </Link>
        </div>
      </div>

      {isTrialing && (
        <div data-testid="billing-trial-status" className="glass rounded-2xl border border-primary/30 bg-primary/5 p-5 mb-6 flex items-center gap-4 flex-wrap">
          <Gift className="w-8 h-8 text-primary flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">
              {trialDaysLeft === 1 ? "Last day of your Pro trial" : `${trialDaysLeft} days left in your Pro trial`}
            </p>
            <p className="text-sm text-muted-foreground">Everything is unlocked right now. Upgrade below to keep full access when your trial ends.</p>
          </div>
        </div>
      )}

      <PricingCards paidOnly onSelect={startCheckout} />

      <p className="text-xs text-muted-foreground text-center mt-6">
        Secure payment via Stripe · cancel anytime from your account.
      </p>
    </div>
  );
};

export default BillingPage;
